/** Normalize time string to H:i (e.g. "14:30") for backend validation */
const toHi = (time) => {
  if (!time || typeof time !== "string") return "";
  const t = time.trim();
  const match = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (match) {
    const h = match[1].padStart(2, "0");
    const m = match[2];
    return `${h}:${m}`;
  }
  return t;
};

const isValidBase64Image = (str) => {
  if (!str || typeof str !== "string") return false;
  return /^data:image\/(jpeg|jpg|png|webp|gif);base64,/.test(str);
};

const isImageUrl = (image) => {
  if (!image || typeof image !== "string") return false;
  return /^https?:\/\//.test(image);
};

const processImage = (image) => {
  if (!image) return null;
  if (isImageUrl(image)) {
    return image;
  }
  if (isValidBase64Image(image)) {
    return image;
  }
  if (typeof image === "string" && image.length > 0) {
    return `data:image/jpeg;base64,${image}`;
  }

  return null;
};

/**
 * Transform form data to API payload
 */
export const transformConferenceTradeMissionPayload = (formData, eventType) => {
  const startDate = formData.eventStartDate || formData.eventDate || "";
  const endDate = formData.eventEndDate || formData.eventStartDate || formData.eventDate || "";
  const startTime = toHi(formData.eventStartTime || formData.eventTime || "");
  const endTime = toHi(formData.eventEndTime || formData.eventStartTime || formData.eventTime || "");
  const payload = {
    event_type: [eventType],
    organizer_slug: formData.organizer?.slug || formData.organizer_slug || "",
    event_title: formData.eventName || "",
    event_start_date: startDate,
    event_end_date: endDate,
    event_start_time: startTime,
    event_end_time: endTime,
    venue_name: formData.venueName || "",
    venue_street_address: formData.venueStreetAddress || "",
    country: formData.country || "",
    city: formData.city || "",
    venue_google_location: formData.venueGoogleLocation || "",
    // Media: URLs if unchanged, base64 if changed
    thumbnail: processImage(formData.thumbnail),
    featured_images: (formData.featuredImages || [])
      .map((img) => processImage(img))
      .filter(Boolean),
    about_event: formData.aboutEvent || "",
    performers: (formData.performers || []).map((p) =>
      typeof p === "string" ? { name: p, image: null } : { name: p?.name || "", image: processImage(p?.image) }
    ),
    sponsors: (formData.sponsors || [])
      .map((sponsor) => ({
        name: sponsor.name || "",
        logo: processImage(sponsor.logo),
      }))
      .filter((sponsor) => sponsor.logo !== null),
    itinerary: (formData.itinerary || []).map((item) => ({
      title: item.title || "",
      time: item.time || "",
      description: item.description || "",
    })),
  };

  // Trade Mission specific fields
  if (eventType === "trade_mission") {
    payload.overall_slots =
      parseInt(formData.totalParticipantSlots) || 0;
    payload.ticket_types = [];
    payload.motivational_questions = [];
  }

  // Conference specific fields
  if (eventType === "conference") {
    // Handle new section-based format
    payload.motivational_questions = (formData.motivationalQuestions || []).map(
      (section) => ({
        sectionTitle: section.sectionTitle || "",
        questions: (section.questions || []).map((q) => ({
          question: q.question || "",
          type: q.type || "short_text",
          required: q.required !== undefined ? q.required : true,
          placeholder: q.placeholder || "",
          ...([
            "single_choice",
            "multiple_choice",
            "dropdown",
          ].includes(q.type) && {
            options: q.options || [],
          }),
        })),
      })
    );

    // Conference tickets - only include if overallSlots is provided
    if (formData.overallSlots) {
      payload.overall_slots = parseInt(formData.overallSlots);
      payload.ticket_types = (formData.ticketTypes || []).map((ticket) => ({
        name: ticket.name,
        price: parseFloat(ticket.price),
        currency: ticket.currency || "GHS",
        slots_allocated: parseInt(ticket.slotsAllocated),
        description: ticket.description,
        whats_included: Array.isArray(ticket.whatsIncluded)
          ? ticket.whatsIncluded
          : [],
      }));
    } else {
      payload.overall_slots = null;
      payload.ticket_types = [];
    }
  }

  return payload;
};

const formatDateForInput = (dateString) => {
  if (!dateString) return "";

  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return "";
    }

    // Format to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.warn("Error formatting date:", error);
    return "";
  }
};

/**
 * Transform API response to form data
 */
export const transformConferenceTradeMissionToFormData = (
  data,
  organizer = null
) => {
  if (!data) return null;

  // Determine event type from data
  const eventTypes = Array.isArray(data.event_type)
    ? data.event_type
    : [data.event_type];
  const isConference = eventTypes.includes("conference");
  const isTradeMission = eventTypes.includes("trade_mission");

  return {
    eventType: isConference
      ? "conference"
      : isTradeMission
      ? "trade_mission"
      : null,
    organizer: organizer || data.organizer || null,
    eventName: data.event_title || "",
    eventDate: formatDateForInput(data.event_date),
    eventStartDate: formatDateForInput(data.event_start_date || data.event_date),
    eventEndDate: formatDateForInput(data.event_end_date || data.event_date),
    eventTime: data.event_time || "",
    eventStartTime: toHi(data.event_start_time || data.event_time || ""),
    eventEndTime: toHi(data.event_end_time || data.event_time || ""),
    venueName: data.venue_name || "",
    venueStreetAddress: data.venue_street_address || "",
    country: data.country || "",
    city: data.city || "",
    venueGoogleLocation: data.venue_google_location || "",
    thumbnail: data.thumbnail || null,
    featuredImages: Array.isArray(data.featured_images)
      ? data.featured_images
      : [],
    aboutEvent: data.about_event || "",
    performers: Array.isArray(data.performers)
      ? data.performers.map((p) =>
          typeof p === "string" ? { name: p, image: null } : { name: p?.name || "", image: p?.image || null }
        )
      : [],
    sponsors: Array.isArray(data.sponsors)
      ? data.sponsors.map((sponsor) => ({
          name: sponsor.name || "",
          logo: sponsor.logo || null,
        }))
      : [],
    itinerary: Array.isArray(data.itinerary)
      ? data.itinerary.map((item) => ({
          title: item.title || "",
          time: item.time || "",
          description: item.description || "",
        }))
      : [],
    // For trade missions, overall_slots maps to totalParticipantSlots
    totalParticipantSlots: isTradeMission && data.overall_slots
      ? String(data.overall_slots)
      : "",
    motivationalQuestions: Array.isArray(data.motivational_questions)
      ? data.motivational_questions.map((section) => {
          // Check if it's the new section-based format or old flat format
          if (section.sectionTitle !== undefined) {
            return {
              sectionTitle: section.sectionTitle || "",
              questions: Array.isArray(section.questions)
                ? section.questions.map((q) => ({
                    question: q.question || "",
                    type: q.type || "short_text",
                    required: q.required !== undefined ? q.required : true,
                    placeholder: q.placeholder || "",
                    options: Array.isArray(q.options) ? q.options : [],
                  }))
                : [],
            };
          } else {
            // Old format - backward compatibility
            return {
              question: section.question || "",
              type: section.type || "free_text",
              options: Array.isArray(section.options) ? section.options : [],
            };
          }
        })
      : [],
    // Conference ticket fields - for conferences, overall_slots maps to overallSlots
    overallSlots: isConference && data.overall_slots ? String(data.overall_slots) : "",
    ticketTypes: Array.isArray(data.ticket_types)
      ? data.ticket_types.map((ticket) => ({
          name: ticket.name || "",
          price: ticket.price ? String(ticket.price) : "",
          currency: ticket.currency || "GHS",
          slotsAllocated: ticket.slots_allocated
            ? String(ticket.slots_allocated)
            : "",
          description: ticket.description || "",
          whatsIncluded: Array.isArray(ticket.whats_included)
            ? ticket.whats_included
            : [],
        }))
      : [],
  };
};
